/* Copyright (c) 2021-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import * as React from "react";
import { useGetDiscoveryRulesQuery, DiscoveryRule } from "~/generated/graphql";
import { ERROR_TEXT } from "~/constants/text";
import {
  useAddDiscoveryRule,
  useRemoveDiscoveryRule,
  useUpdateDiscoveryRule,
} from "./graphql";
import { ACTION } from "./types";
import {
  mapQueryRules,
  getRuleSubmissionDiff,
  mapFormData,
  filterEmptyRules,
} from "./utils";

type Props = {
  envId: string;
  onSuccess?: () => void;
};

type ReturnValues = {
  loading: boolean;
  error: string;
  rules: DiscoveryRule[];
  update: (newRules: DiscoveryRule[]) => void;
};

const useDiscoveryRules = ({
  envId,
  onSuccess = () => undefined,
}: Props): ReturnValues => {
  const [isLoading, setIsLoading] = React.useState(false);
  const [error, setError] = React.useState("");

  const { data } = useGetDiscoveryRulesQuery({
    variables: { envId },
    onError: () => {
      setError(ERROR_TEXT.failedUpdatingDiscoveryRules);
    },
  });

  const [addDiscoveryRule] = useAddDiscoveryRule({
    onError: (err) => {
      setError(err.message);
    },
  });

  const [removeDiscoveryRule] = useRemoveDiscoveryRule({
    onError: () => {
      setError(ERROR_TEXT.failedUpdatingDiscoveryRules);
    },
  });

  const [updateDiscoveryRule] = useUpdateDiscoveryRule({
    onError: (err) => {
      setError(err.message);
    },
  });

  const update = async (newRules: DiscoveryRule[]) => {
    setIsLoading(true);
    setError("");
    const prevRules = mapQueryRules(data?.discoveryRules);

    const rulesWithActions = getRuleSubmissionDiff(
      prevRules,
      filterEmptyRules(newRules)
    );

    const requests = rulesWithActions.map((ruleAction) => {
      if (ruleAction.action === ACTION.ADD) {
        const {
          __typename: ignoredTypename,
          id: ignoredId,
          applicationId: ignoredApplicationId,
          environmentId: ignoredEnvironmentId,
          ...ruleBody
        } = ruleAction.rule;

        return addDiscoveryRule(
          {
            input: {
              ...ruleBody,
              environmentId: envId,
            },
          },
          onSuccess
        );
      }

      if (ruleAction.action === ACTION.REMOVE) {
        return removeDiscoveryRule(
          {
            id: ruleAction.rule?.id ?? "",
            environmentId: envId,
          },
          onSuccess
        );
      }

      if (ruleAction.action === ACTION.UPDATE) {
        const {
          __typename: ignoredTypename,
          applicationId: ignoredApplicationId,
          environmentId: ignoredEnvironmentId,
          ...ruleBody
        } = ruleAction.rule;

        return updateDiscoveryRule(
          {
            input: {
              ...ruleBody,
            },
          },
          onSuccess
        );
      }

      return Promise.resolve(undefined);
    });

    await Promise.all<Promise<undefined> | ReturnType<typeof addDiscoveryRule>>(
      requests
    );

    setIsLoading(false);
  };

  return {
    loading: isLoading,
    error,
    rules: mapFormData(mapQueryRules(data?.discoveryRules)),
    update,
  };
};

export default useDiscoveryRules;
